import {
  AccountType,
  AircraftRestriction,
  BatteryType,
  BatteryTypeVariant,
  BusinessType,
  CancelReason,
  CargoCooling,
  CargoCoolingRange,
  CargoType,
  ClarificationReason,
  CostUnitType,
  DataType,
  DimensionUnit,
  DocumentType,
  DrawerType,
  EarliestTenderOption,
  EstimatedTransitTime,
  ExpiryReason,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  HandlingOptions,
  InlandCategory,
  MarkupCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  QuoteStatus,
  RateCategory,
  RateImportState,
  RequestStatus,
  Section,
  ServiceType,
  SourceType,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TimeOfDay,
  UnNumber,
  VariantType,
  WeightType,
  WeightUnit,
} from '@pelicargo/types';
import { PermissionLevel } from '@pelicargo/utils';
import {
  AircraftType,
  BacklogStatus,
  CargoType as PrismaCargoType,
  CargoTypeSub,
  DayOfWeek,
  FlightSegmentBookingStatus,
  IATAFlightSegmentStatus,
  IdentitySystem,
  InstructionUsage,
  LineItemAudience,
  LineItemType,
  MarkupConfigurationType,
  NotificationCategory,
  PaymentStatus,
  ProviderDataMapType,
  QuoteSource,
  ShipmentFormPayment,
  ShipmentStatus,
  TrackingShipmentStatus,
} from '@prisma/client';

import { isInlandCategory, isMarkupCategory, isRateCategory, isSurchargeCategory } from '../models/line-item';
import { prettyNumber } from './pretty-number';

export const getPrettyCargoCooling = (option: CargoCooling): string => {
  switch (option) {
    case CargoCooling.GEL_PACKS:
      return 'Gel Packs';
    case CargoCooling.DRY_ICE:
      return 'Dry Ice';
    case CargoCooling.NO_COOLING:
      return 'No Additional Cooling';
    default:
      return '';
  }
};

export const getPrettyCargoTempRange = (option: CargoCoolingRange): string => {
  switch (option) {
    case CargoCoolingRange.NEGATIVE_TEN_TO_NEGATIVE_TWENTY_CELSIUS:
      return '-10 to -20 °C';
    case CargoCoolingRange.TWO_TO_EIGHT_CELSIUS:
      return '+2 to +8 °C';
    case CargoCoolingRange.TWO_TO_TWENTY_FIVE_CELSIUS:
      return '+2 to +25 °C';
    case CargoCoolingRange.FIFTEEN_TO_TWENTY_FIVE_CELSIUS:
      return '+15 to +25 °C';
    default:
      return '';
  }
};

export const getPrettyDataType = (option: DataType): string => {
  switch (option) {
    case DataType.STRING:
      return 'String or Enum';
    case DataType.BOOLEAN:
      return 'Boolean';
    case DataType.DATE:
      return 'Date';
    case DataType.NUMBER:
      return 'Number';
    case DataType.JSON:
      return 'JSON';
    case DataType.IGNORE:
      return 'IGNORE';
    default:
      return '';
  }
};

export const getPrettyIdentitySystem = (option: IdentitySystem): string => {
  switch (option) {
    case IdentitySystem.IATA:
      return 'IATA';
    case IdentitySystem.SCAC:
      return 'SCAC';
    case IdentitySystem.MC:
      return 'MC';
    case IdentitySystem.USDOT:
      return 'USDOT';
    default:
      return '';
  }
};

export const getPrettyHandling = (option: string): string => {
  switch (option) {
    case HandlingOptions.Stackable:
      return 'Stackable';
    case HandlingOptions.NonStackable:
      return 'Non Stackable';
    case HandlingOptions.Drums:
      return 'Packed in Drums';
    case HandlingOptions.Pails:
      return 'Packed in Pails';
    case HandlingOptions.Supersacks:
      return 'Packed in Supersacks';

    // Deprecated
    case HandlingOptions.TopLoadOnly:
      return 'Top Load Only';
    case HandlingOptions.RequiredOrientation:
      return 'Required Orientation';
    case HandlingOptions.Fragile:
      return 'Fragile';
    default:
      return '';
  }
};

export const getPrettyCargoType = (option: CargoType | PrismaCargoType): string => {
  switch (option) {
    case CargoType.GeneralCargo:
      return 'General Cargo';
    case CargoType.DangerousGoods:
      return 'Dangerous Goods';
    case CargoType.Perishable:
      return 'Temperature Controlled';
    case CargoType.Batteries:
      return 'Batteries';
    case CargoType.Pharma:
      return 'Pharma';
    case CargoType.Valuable:
      return 'Valuable';
    default:
      return '';
  }
};
export const getPrettyCargoTypeSub = (option: CargoTypeSub): string => {
  switch (option) {
    case CargoTypeSub.GENERAL:
      return 'General';
    case CargoTypeSub.PHARMA:
      return 'Pharmaceuticals';
    case CargoTypeSub.PERISHABLE:
      return 'Perishables';
    default:
      return '';
  }
};

export const getPrettySurchargeUnit = (option: SurchargeUnit): string => {
  switch (option) {
    case SurchargeUnit.AWB:
      return 'Unit';
    case SurchargeUnit.KG:
      return 'kg';
    case SurchargeUnit.UN:
      return 'UN';
    case SurchargeUnit.FLAT:
      return 'Flat';
    default:
      return '';
  }
};

export const getPrettyCostUnitType = (option: CostUnitType): string => {
  switch (option) {
    case CostUnitType.CURRENCY:
      return '$';
    case CostUnitType.PERCENTAGE:
      return '%';
    default:
      return '';
  }
};

export const getPrettySurchargeWeightType = (option: SurchargeWeightType): string => {
  switch (option) {
    case SurchargeWeightType.CHARGEABLE_WEIGHT:
      return 'Chargeable Weight';
    case SurchargeWeightType.GROSS_WEIGHT:
      return 'Gross Weight';
    default:
      return '';
  }
};

export const getPrettyServiceType = (option: ServiceType): string => {
  switch (option) {
    case ServiceType.General:
      return 'General';
    case ServiceType.Expedited:
      return 'Expedited';
    case ServiceType.Urgent:
      return 'Urgent';
    case ServiceType.Other:
      return 'Other';
    default:
      return '';
  }
};

export const getPrettySurchargeCategory = (option: SurchargeCategory): string => {
  switch (option) {
    case SurchargeCategory.FUEL:
      return 'Fuel';
    case SurchargeCategory.FWL_FHL_EDI:
      return 'FWL/FHL EDI';
    case SurchargeCategory.NON_EAWB:
      return 'Non-EAWB';
    case SurchargeCategory.SCREENING:
      return 'Screening';
    case SurchargeCategory.SECURITY:
      return 'Security';
    case SurchargeCategory.TRUCKING:
      return 'Trucking';
    case SurchargeCategory.UN_FEE:
      return 'UN Fee';
    case SurchargeCategory.LI_BATTERY:
      return 'Lithium Battery Fee';
    case SurchargeCategory.DRY_ICE:
      return 'Dry Ice Fee';
    case SurchargeCategory.NON_STACKABLE:
      return 'Non-Stackable Fee';
    case SurchargeCategory.UNKNOWN_SHIPPER:
      return 'Unknown Shipper Fee';
    case SurchargeCategory.MAINDECK:
      return 'Maindeck Fee';
    case SurchargeCategory.OVERLENGTH:
      return 'Overlength Fee';
    case SurchargeCategory.MANUAL_BOOKING:
      return 'Manual Booking Fee';
    case SurchargeCategory.DISRUPTION:
      return 'Disruption Fee';
    case SurchargeCategory.PICK_UP:
      return 'Pick Up Fee';
    case SurchargeCategory.AWB_LABELING:
      return 'AWB Labeling Fee';
    case SurchargeCategory.TRANSFER:
      return 'Transfer Fee';
    case SurchargeCategory.HAZARDOUS:
      return 'Hazardous Fee';
    case SurchargeCategory.DOCUMENT_PRINTING:
      return 'Document Printing Fee';
    default:
      return option;
  }
};

export const getPrettyRateCategory = (option: RateCategory): string => {
  switch (option) {
    case RateCategory.TARIFF:
      return 'Tariff';
    case RateCategory.CONTRACT:
      return 'Contract';
    case RateCategory.PROMO:
      return 'Promo';
    case RateCategory.SPOT:
      return 'Spot';
    default:
      return option;
  }
};

export const getPrettyInlandCategory = (option: InlandCategory): string => {
  switch (option) {
    case InlandCategory.AWB_LABELING:
      return 'Label Preparation Fee';
    case InlandCategory.PICK_UP:
      return 'Pick Up & Transfer (TSA)';
    case InlandCategory.TRANSFER:
      return 'Cross Dock & Transfer';
    default:
      return option;
  }
};

export const getPrettyLineItemType = (option: LineItemType): string => {
  if (isRateCategory(option)) return getPrettyRateCategory(option);
  if (isSurchargeCategory(option)) return getPrettySurchargeCategory(option);
  if (isMarkupCategory(option)) return getPrettyMarkupCategory(option);
  if (isInlandCategory(option)) return getPrettyInlandCategory(option);
  if (LineItemType.OTHER === option) return 'Other';
  return '';
};

export const getPrettyBacklogStatus = (option: BacklogStatus): string => {
  switch (option) {
    case BacklogStatus.AtRisk:
      return 'At Risk';
    case BacklogStatus.Backlogged:
      return 'Backlogged';
    case BacklogStatus.None:
      return 'None';
    default:
      return '';
  }
};

export const getPrettyEstimatedTransitTime = (option: EstimatedTransitTime): string => {
  switch (option) {
    case EstimatedTransitTime.OneToTwoDays:
      return '1-2 Days';
    case EstimatedTransitTime.TwoToThreeDays:
      return '2-3 Days';
    case EstimatedTransitTime.ThreeToFourDays:
      return '3-4 Days';
    case EstimatedTransitTime.FourToFiveDays:
      return '4-5 Days';
    case EstimatedTransitTime.FiveToSixDays:
      return '5-6 Days';
    case EstimatedTransitTime.SixToSevenDays:
      return '6-7 Days';
    case EstimatedTransitTime.SevenDaysPlus:
      return '7+ Days';
    default:
      return '';
  }
};

export const getPrettyVariantType = (option: VariantType): string => {
  switch (option) {
    case VariantType.General:
      return 'General';
    case VariantType.Expedited:
      return 'Expedited';
    case VariantType.Urgent:
      return 'Urgent';
    case VariantType.Other:
      return 'Other';
    default:
      return '';
  }
};

export const getPrettyQuoteStatus = (option: QuoteStatus): string => {
  switch (option) {
    case QuoteStatus.Pending:
      return 'Pending';
    case QuoteStatus.SentToCarrier:
      return 'Sent to Carrier';
    case QuoteStatus.ReadyForQC:
      return 'Ready for QC';
    case QuoteStatus.InProgress:
      return 'In Progress';
    case QuoteStatus.Confirmed:
      return 'Confirmed';
    case QuoteStatus.Expired:
      return 'Expired';
    case QuoteStatus.BookingPending:
      return 'Booking Pending';
    case QuoteStatus.BookingRequested:
      return 'Booking Requested';
    default: {
      const check: never = option;
      console.log('Unknown QuoteStatus', check);
      return '';
    }
  }
};

export const getPrettyQuoteSource = (option: QuoteSource): string => {
  switch (option) {
    case QuoteSource.ECHO:
      return 'Echo';
    case QuoteSource.NEXTGEN:
      return 'NextGen';
    case QuoteSource.FORWARDAIR:
      return 'ForwardAir';
    case QuoteSource.GLOBALTRANZ:
      return 'GlobalTranz';
    case QuoteSource.EK:
      return 'Emirates';
    case QuoteSource.AC:
      return 'Air Canada';
    case QuoteSource.QR:
      return 'Qatar Cargo';
    case QuoteSource.SEVENLFREIGHT:
      return '7L Freight';
    case QuoteSource.TK:
      return 'Turkish';
    case QuoteSource.SYSTEM:
      return 'System';
    default: {
      const last: never = option;
      return last;
    }
  }
};

// Customers are slightly different. Not sure why
const customerRequestStatus = {
  [RequestStatus.New]: 'In Progress',
  [RequestStatus.QuotingInProgress]: 'In Progress',
  [RequestStatus.QuotingPending]: 'In Progress',
  [RequestStatus.QuotingComplete]: 'Quotes Ready',
  [RequestStatus.InfoPending]: 'Info Pending',
  [RequestStatus.BookingRequested]: 'Capacity Queued',
  [RequestStatus.BookingChange]: 'Capacity Queued',
  [RequestStatus.BookingPending]: 'Capacity Queued',
  [RequestStatus.BookingComplete]: 'Booked',
  [RequestStatus.BookingCancel]: 'Capacity Queued',
  [RequestStatus.Cancelled]: 'Cancelled',
} satisfies Record<RequestStatus, string>;

export const getPrettyRequestStatus = (option: RequestStatus, customer = false) => {
  if (customer) {
    return customerRequestStatus[option] ?? '-';
  }
  switch (option) {
    case RequestStatus.New:
      return 'New';
    case RequestStatus.QuotingPending:
      return 'Quoting Pending';
    case RequestStatus.QuotingInProgress:
      return 'Quoting In Progress';
    case RequestStatus.QuotingComplete:
      return 'Quoting Complete';
    case RequestStatus.BookingRequested:
      return 'Booking Requested';
    case RequestStatus.BookingPending:
      return 'Booking Pending';
    case RequestStatus.BookingComplete:
      return 'Booking Complete';
    case RequestStatus.BookingChange:
      return 'Booking Change';
    case RequestStatus.BookingCancel:
      return 'Booking Cancel';
    case RequestStatus.InfoPending:
      return 'Info Pending';
    case RequestStatus.Cancelled:
      return 'Cancelled';
    default:
      return '';
  }
};

export const getPrettyWeightUnit = (option: WeightUnit): string => {
  switch (option) {
    case WeightUnit.kg:
      return 'kg';
    case WeightUnit.lb:
      return 'lb';
    default:
      return '';
  }
};

export const getPrettyDimensionUnit = (option: DimensionUnit): string => {
  switch (option) {
    case DimensionUnit.cm:
      return 'cm';
    case DimensionUnit.in:
      return 'in';
    default:
      return '';
  }
};

export const getPrettyAircraftType = (option: AircraftType): string => {
  switch (option) {
    case AircraftType.Freighter:
      return 'Freighter';
    case AircraftType.NarrowBody:
      return 'Narrow Body';
    case AircraftType.RFS:
      return 'RFS';
    case AircraftType.WideBody:
      return 'Wide Body';
    default:
      return '';
  }
};

export const getPrettyInstructionUsage = (option: InstructionUsage) => {
  switch (option) {
    case InstructionUsage.BOOKING:
      return 'Booking';
    case InstructionUsage.QUOTING:
      return 'Quoting';
    default: {
      const exhaustionCheck: never = option;
      return exhaustionCheck;
    }
  }
};

export const getPrettyAircraftRestriction = (type: AircraftRestriction): string => {
  switch (type) {
    case AircraftRestriction.CAO:
      return 'CAO';
    case AircraftRestriction.PAX:
      return 'PAX';
    default:
      return '';
  }
};

export const getPrettyFreightStatus = (option: FreightStatus): string => {
  switch (option) {
    case FreightStatus.Quoting:
      return 'Quoting';
    case FreightStatus.Confirmed:
      return 'Confirmed';
    default:
      return '';
  }
};

export const getPrettyExpiryReason = (option: ExpiryReason): string => {
  switch (option) {
    case ExpiryReason.INVALID_ENTRY:
      return 'Invalid Entry';
    case ExpiryReason.ROUTE_ENDED:
      return 'Route Ended';
    case ExpiryReason.SUCCEEDED:
      return 'Succeeded';
    default:
      return '';
  }
};

export const getPrettySourceType = (option: SourceType): string => {
  switch (option) {
    case SourceType.GOOGLE_SHEETS:
      return 'Google Sheet';
    default:
      return '';
  }
};

export const getPrettyBatteryType = (option: BatteryType): string => {
  switch (option) {
    case BatteryType.DG_BATTERIES_IN_EQUIPMENT:
      return 'DG Batteries';
    case BatteryType.NON_RESTRICTED_BATTERIES_IN_EQUIPMENT:
      return 'Non-Restricted Batteries';
    default:
      return '';
  }
};

export const getPrettyBatteryTypeVariant = (option: BatteryTypeVariant): string => {
  switch (option) {
    case BatteryTypeVariant.LiIon_UN3481_PI967:
      return 'Li-ion: UN3481 | PI967 | Sec II';
    case BatteryTypeVariant.LiIon_UN3481_PI966:
      return 'Li-ion: UN3481 | PI966 | Sec II';
    case BatteryTypeVariant.LiMetal_UN3091_PI969:
      return 'Li metal: UN3091 | PI969 | Sec II';
    case BatteryTypeVariant.LiMetal_UN3091_PI970:
      return 'Li metal: UN3091 | PI970 | Sec II';
    default:
      return '';
  }
};

export const getPrettyPackingInstruction = (option: PackingInstruction): string => {
  switch (option) {
    case PackingInstruction.PI965:
      return '965';
    case PackingInstruction.PI966:
      return '966';
    case PackingInstruction.PI967:
      return '967';
    case PackingInstruction.PI968:
      return '968';
    case PackingInstruction.PI969:
      return '969';
    case PackingInstruction.PI970:
      return '970';

    default:
      return '';
  }
};

export const getPrettyUnNumber = (option: UnNumber): string => {
  switch (option) {
    case UnNumber.UN3480:
      return '3480';
    case UnNumber.UN3481:
      return '3481';
    case UnNumber.UN3090:
      return '3090';
    case UnNumber.UN3091:
      return '3091';
    case UnNumber.UN3171:
      return '3171';

    default:
      return '';
  }
};

export const getPrettyPackingGroup = (option: PackingGroup): string => {
  switch (option) {
    case PackingGroup.I:
      return 'I';
    case PackingGroup.II:
      return 'II';
    case PackingGroup.III:
      return 'III';

    default:
      return '';
  }
};

export const getPrettySection = (option: Section): string => {
  switch (option) {
    case Section.I:
      return 'I';
    case Section.II:
      return 'II';
    case Section.IA:
      return 'IA';
    case Section.IB:
      return 'IB';

    default:
      return '';
  }
};

export const getPrettyDocumentType = (option: DocumentType): string => {
  switch (option) {
    case DocumentType.DGD:
      return 'DGD';
    case DocumentType.MSDS:
      return 'MSDS';
    case DocumentType.AIRWAY_BILL:
      return 'Airway Bill';
    case DocumentType.SHIPPER_DOCUMENTS:
      return 'Shipper Documents';
    case DocumentType.SLI:
      return 'SLI';
    case DocumentType.LABELS:
      return 'Labels';
    case DocumentType.BOL:
      return 'BOL';
    case DocumentType.PROVIDER_SPOT_QUOTE:
      return 'Provider Spot Quote';
    case DocumentType.OTHER:
      return 'Other';
    case DocumentType.MANIFEST:
      return 'Manifest';
    case DocumentType.HOUSE_AIRWAY_BILL:
      return 'HAWB';
    case DocumentType.INVOICE:
      return 'Invoice';
    default:
      return '';
  }
};

export const getPrettyAccountType = (option: AccountType): string => {
  switch (option) {
    case AccountType.IATA:
      return 'IATA';
    case AccountType.IAC:
      return 'IAC';
    case AccountType.UNCERTIFIED:
      return 'Uncertified';
    case AccountType.ORGANIZATION:
      return 'Organization';

    default:
      return '';
  }
};

export const getPrettyUploader = (business_type: BusinessType): string => {
  switch (business_type) {
    case BusinessType.FREIGHT_FORWARDER:
      return 'FF';
    case BusinessType.AIRLINE:
      return 'Airline';
    case BusinessType.GSA:
      return 'GSA';

    default:
      return '';
  }
};

export const getPrettyBusinessType = (option: BusinessType): string => {
  switch (option) {
    case BusinessType.FREIGHT_FORWARDER:
      return 'Freight Forwarder';
    case BusinessType.GSA:
      return 'General Sales Agent (GSA)';
    case BusinessType.AIRLINE:
      return 'Airline';

    default:
      return '';
  }
};

export const getPrettyTrackingShipmentStatus = (option: TrackingShipmentStatus): string => {
  switch (option) {
    case TrackingShipmentStatus.ACTIVE:
      return 'Active';
    case TrackingShipmentStatus.REPLACED:
      return 'Replaced';
    case TrackingShipmentStatus.COMPLETED:
      return 'Completed';
    default:
      return '';
  }
};

export const getPrettyShipmentStatus = (option: ShipmentStatus): string => {
  switch (option) {
    case ShipmentStatus.AIRLINE_ACCEPTED:
      return 'Airline Accepted';
    case ShipmentStatus.ARRIVED:
      return 'Arrived';
    case ShipmentStatus.AT_WAREHOUSE:
      return 'At Warehouse';
    case ShipmentStatus.DELIVERED:
      return 'Delivered';
    case ShipmentStatus.DEPARTED:
      return 'Departed';
    case ShipmentStatus.ON_HOLD:
      return 'On Hold';
    case ShipmentStatus.PENDING:
      return 'Pending';
    case ShipmentStatus.PICKUP_COMPLETE:
      return 'Pickup Complete';
    case ShipmentStatus.PICKUP_SCHEDULED:
      return 'Pickup Scheduled';
    case ShipmentStatus.OUT_FOR_DELIVERY:
      return 'Out For Delivery';
    case null:
    case undefined:
      return '-';
    default: {
      const exhaustive: never = option;
      console.log('unknown option for shipment status:', exhaustive);
      return '';
    }
  }
};

export const getPrettyPaymentStatus = (option: PaymentStatus) => {
  switch (option) {
    case PaymentStatus.INVOICED:
      return 'Invoiced';
    case PaymentStatus.NON_APPLICABLE:
      return 'N/A';
    case PaymentStatus.OVERDUE:
      return 'Overdue';
    case PaymentStatus.PAID:
      return 'Paid';
    case PaymentStatus.PENDING:
      return 'Pending';
    case PaymentStatus.READY_TO_INVOICE:
      return 'Ready to invoice';
    case null:
      return '-';
    default:
      return '';
  }
};

export const getPrettyRateImportState = (option: RateImportState): string => {
  switch (option) {
    case RateImportState.PENDING:
      return 'Pending';
    case RateImportState.RUNNING:
      return 'Running';
    case RateImportState.SUCCESS:
      return 'Success';
    case RateImportState.EXPIRED:
      return 'Expired';
    case RateImportState.ERROR:
      return 'Error';
    default:
      return '';
  }
};

export const getPrettyTimeOfDay = (option: TimeOfDay) => {
  switch (option) {
    case TimeOfDay.MORNING:
      return 'Morning';
    case TimeOfDay.MID_DAY:
      return 'Day';
    case TimeOfDay.EVENING:
      return 'Evening';
    default:
      return '';
  }
};

export const getPrettyEarliestTenderOption = (option: EarliestTenderOption) => {
  switch (option) {
    case EarliestTenderOption.ANYTIME:
      return 'Anytime';
    case EarliestTenderOption.TWENTY_FOUR_HOURS:
      return '24hrs';
    case EarliestTenderOption.FOURTY_EIGHT_HOURS:
      return '48hrs';
    case EarliestTenderOption.SEVENTY_TWO_HOURS:
      return '72hrs';
    default:
      return '';
  }
};

export const getPrettyOnboardingStep = (option: OnboardingStep) => {
  switch (option) {
    case OnboardingStep.CONFIRM:
      return 'Confirm';
    case OnboardingStep.PERSONAL:
      return 'Personal';
    case OnboardingStep.COMPANY_SELECT:
      return 'Company Select';
    case OnboardingStep.COMPANY:
      return 'Company';
    case OnboardingStep.SIGNATURE:
      return 'Signature';
    case OnboardingStep.INVITE:
      return 'Invite';
    case OnboardingStep.COMPLETE:
      return 'Complete';
  }
};

export const getPrettyClarificationReason = (option: ClarificationReason) => {
  switch (option) {
    case ClarificationReason.IncorrectDetails:
      return "The shipment's pieces, weights, and/or dimensions are incorrect.";
    case ClarificationReason.Oversize:
      return 'The shipment is oversized and requires more information.';
    case ClarificationReason.DangerousGoods:
      return 'The shipment involves dangerous goods and requires more information.';
    case ClarificationReason.TemperatureControl:
      return 'The shipment involves temperature control and requires more information.';
    case ClarificationReason.Other:
      return 'Other (must provide all information)';
  }
};

export const getPrettyClarificationMessage = (
  option: ClarificationReason,
  numPieces: number | string,
  grossWeight: number | string,
  preferredWeightUnit: WeightUnit | string,
) => {
  const prettyGrossWeight =
    typeof grossWeight === 'number' ? prettyNumber(grossWeight, { maximumFractionDigits: 1 }) : grossWeight;

  switch (option) {
    case ClarificationReason.IncorrectDetails:
      return `Thank you for your request. Your shipment's pieces, weights, and dimensions are flagged as potentially incorrect.
Could you please confirm this is ${numPieces} piece(s) at ${prettyGrossWeight} ${preferredWeightUnit} total weight?
If so, kindly share any additional information (commodity, pictures, packing).`;
    case ClarificationReason.Oversize:
      return `Thank you for your request.  Your shipment requires special handling and additional information is required to quote.
Could you please share additional information such as the specific commodity, pictures, schematics, ready date, or packing description?`;
    case ClarificationReason.DangerousGoods:
      return `Thank you for your request.  Your shipment requires special handling and additional information is required to quote.
Could you please share the Dangerous Goods Declaration (DGD) or any additional information?
 • PAX OK or CAO
 • Class, UN, PG, PI, Section`;
    case ClarificationReason.TemperatureControl:
      return `Thank you for your request.  Your shipment requires special handling and additional information is required to quote.
Could you please clarify:
 • Specific Commodity
 • Pharma, Perishable Food or Agriculture, or Other Temperature Controlled
 • Dry Ice and Dry Ice Quantity`;
    case ClarificationReason.Other:
      return '';
  }
};

export const getPrettyCancelReason = (option: CancelReason) => {
  switch (option) {
    case CancelReason.CargoDelay:
      return 'The cargo is delayed from the shipper.';
    case CancelReason.TransitTime:
      return 'The shipper needed a better transit time.';
    case CancelReason.Rate:
      return 'The agent requested to change carriers based on rate.';
    case CancelReason.Unknown:
      return 'The agent requested to change carriers for an unknown reason.';
  }
};

export const getPrettyMarkupCategory = (option: MarkupCategory) => {
  switch (option) {
    case MarkupCategory.FULFILLMENT_PARTNER_MARKUP:
      return 'FP Markup';
    case MarkupCategory.SYSTEM_MARKUP:
      return 'System Markup';
    case MarkupCategory.TENANT_MARKUP:
      return 'Tenant Markup';
    case MarkupCategory.OTHER_MARKUP:
      return 'Other';
    default:
      return option;
  }
};

export const getPrettyEmailDrawerType = (option: DrawerType) => {
  switch (option) {
    case DrawerType.AirlineBooking:
      return 'Book Quote';
    case DrawerType.ProviderQuoting:
      return 'Request Quote';
    case DrawerType.BookingCancel:
      return 'Cancel Booking';
    case DrawerType.BookingChange:
      return 'Change Booking';
    case DrawerType.BookingConfirmation:
      return 'Send Booking';
    case DrawerType.Contact:
      return 'Contact';
    case DrawerType.NewRate:
      return 'Send New Rate';
    case DrawerType.QuoteUpdate:
      return 'Send Update';
    case DrawerType.RequestClarifications:
      return 'Request Clarifications';
  }
};

export type SortNames = 'status' | 'origin' | 'destination' | 'cost' | 'cost_total' | 'provider_id' | 'created_at';
export const getPrettySortName = (sort: SortNames) => {
  switch (sort) {
    case 'status':
      return 'Status';
    case 'origin':
      return 'Origin';
    case 'destination':
      return 'Destination';
    case 'cost':
      return 'Cost';
    case 'cost_total':
      return 'Cost (Total)';
    case 'provider_id':
      return 'Provider ID';
    case 'created_at':
    default:
      return 'Creation Date';
  }
};

export const getPrettyFreightService = (freightService: FreightService) => {
  switch (freightService) {
    case FreightService.AFR:
      return 'Air Freight Only';
    case FreightService.EXW:
      return 'Include Pickup (EXW)';
    case FreightService.FCA:
      return 'Shipper Delivers (FCA)';
  }
};

export const getPrettyFreightServiceAddOn = (freightServiceAddOn: FreightServiceAddOn) => {
  switch (freightServiceAddOn) {
    case FreightServiceAddOn.AES:
      return 'AES Filing';
    default: {
      const fallbackPrevention: never = freightServiceAddOn;
      return fallbackPrevention;
    }
  }
};

export const getPrettyMarkupConfigurationType = (type: MarkupConfigurationType) => {
  switch (type) {
    case MarkupConfigurationType.FULFILLMENT_PARTNER:
      return 'FP Markup';
    case MarkupConfigurationType.SYSTEM:
      return 'System Markup';
    case MarkupConfigurationType.TENANT:
      return 'Tenant Markup';
  }
};

export const getPrettyWeightType = (weightType: WeightType) => {
  switch (weightType) {
    case WeightType.PerPiece:
      return 'Weight Per Piece';
    case WeightType.Gross:
      return 'Gross Weight';
  }
};

// A little abnormal but it would have been overkill to make an enum for this
export const getPrettyConfirmation = (confirmation: boolean) => {
  return confirmation ? 'Yes' : 'No';
};

export const getPrettyIATAFlightSegmentStatus = (status: IATAFlightSegmentStatus) => {
  return (
    (
      {
        [IATAFlightSegmentStatus.CA]: 'Space allocation against allotment',
        [IATAFlightSegmentStatus.HK]: 'Holding confirmed',
        [IATAFlightSegmentStatus.HL]: 'Holding Wait List',
        [IATAFlightSegmentStatus.HN]: 'Have requested space allocation',
        [IATAFlightSegmentStatus.KK]: 'Space Confirmed',
        [IATAFlightSegmentStatus.LL]: 'Wait list',
        [IATAFlightSegmentStatus.NA]: 'Requesting space allocation; if not available will accept alternative',
        [IATAFlightSegmentStatus.NL]: 'Requesting space allocation; for wait list',
        [IATAFlightSegmentStatus.NN]: 'Requesting space allocation; will not accept alternative',
        [IATAFlightSegmentStatus.SS]: 'Reporting Sale',
        [IATAFlightSegmentStatus.UN]: 'No confirmation',
        [IATAFlightSegmentStatus.UU]: 'Unable to confirm space',
        [IATAFlightSegmentStatus.QU]: 'Unable to confirm queue to FCTL',
        [IATAFlightSegmentStatus.KL]: 'Wait list confirmed',
        [IATAFlightSegmentStatus.KC]: 'Wait list confirmed',
        [IATAFlightSegmentStatus.LA]: 'Stand by virtual pending confirm',
        [IATAFlightSegmentStatus.XX]: 'Cancel previous space allocation',
        [IATAFlightSegmentStatus.AS]: 'Assigned',
        [IATAFlightSegmentStatus.CN]: 'Cancellation noted',
        [IATAFlightSegmentStatus.AA]: 'Lying Itinerary',
        [IATAFlightSegmentStatus.NS]: 'No Show',
      } as const satisfies Record<IATAFlightSegmentStatus, string>
    )?.[status] ?? status
  );
};

export const getPrettyFlightSegmentBookingStatus = (status: FlightSegmentBookingStatus) => {
  return (
    {
      [FlightSegmentBookingStatus.Cancelled]: 'Cancelled',
      [FlightSegmentBookingStatus.Confirmed]: 'Confirmed',
      [FlightSegmentBookingStatus.Pending]: 'Pending',
      [FlightSegmentBookingStatus.Unknown]: 'Unknown',
    } as const satisfies Record<FlightSegmentBookingStatus, string>
  )?.[status];
};

export const getPrettyProviderDataMap = (type: ProviderDataMapType) => {
  return (
    {
      [ProviderDataMapType.API_FLIGHT_SEGMENT_STATUS_TO_PELI_FLIGHT_SEGMENT_STATUS]: 'Booking Status',
      [ProviderDataMapType.API_SURCHARGE_NAME_TO_PELI_SURCHARGE_TYPE]: 'Surcharge',
    } as const satisfies Record<ProviderDataMapType, string>
  )?.[type];
};

export const getPrettyNotificationCategory = (category: NotificationCategory): string => {
  return (
    {
      [NotificationCategory.Booking]: 'Booking emails',
      [NotificationCategory.Quote]: 'Quote emails',
      [NotificationCategory.All]: 'All emails',
      [NotificationCategory.Primary]: 'Primary contact',
      [NotificationCategory.Marketplace]: 'Marketplace emails',
      [NotificationCategory.TrackAndTrace]: 'Shipment updates',
    } as const satisfies Record<NotificationCategory, string>
  )?.[category];
};

export const getDayOfWeek = (date: Date) => {
  return [
    DayOfWeek.SUNDAY,
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
  ][date.getDay()];
};

export const getPrettyPermissionLevel = (level: PermissionLevel) => {
  switch (level) {
    case PermissionLevel.ACCOUNTMEMBER:
      return 'Account Member';
    case PermissionLevel.CSMEMBER:
      return 'Customer Success Member';
    case PermissionLevel.PUBLIC:
      return 'Public/No access';
    case PermissionLevel.SUPERADMIN:
      return 'Super Admin';
    default: {
      const l: never = level;
      console.log('Unknown level', l);
      return l;
    }
  }
};

export const getPrettyShipmentFormPayment = (shipmentFormPayment: ShipmentFormPayment) => {
  switch (shipmentFormPayment) {
    case ShipmentFormPayment.PRE_PAID:
      return 'Prepaid';
    case ShipmentFormPayment.COLLECT:
      return 'Collect';
    default: {
      const l: never = shipmentFormPayment;
      console.log('Unknown shipment form payment', l);
      return l;
    }
  }
};

export const getPrettyLineItemAudience = (audience: LineItemAudience) => {
  switch (audience) {
    case LineItemAudience.CUSTOMER:
      return 'Customer';
    case LineItemAudience.FULFILLMENT_PARTNER:
      return 'Fulfillment Partner';
    case LineItemAudience.TENANT:
      return 'Tenant';
    case LineItemAudience.SYSTEM:
      return 'System';
    default: {
      const a: never = audience;
      console.log('Unknown audience', a);
      return a;
    }
  }
};
